import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment } from '@mui/material';

/**
 * PriceField component - Handles price input with VAT calculations
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string} props.value - Price value (VAT exclusive)
 * @param {function} props.onChange - Change handler (returns VAT exclusive value)
 * @param {boolean} props.isVatInclusive - Whether display value should include VAT
 * @param {number} props.vatRate - VAT rate percentage
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {Object} props.sx - Additional styles
 */
const PriceField = ({ 
  label, 
  value, 
  onChange, 
  isVatInclusive, 
  vatRate, 
  disabled = false,
  sx = {} 
}) => {
  const [displayValue, setDisplayValue] = useState('');
  
  // Update display value when value or VAT setting changes
  useEffect(() => {
    if (value === '' || value === null || value === undefined) {
      setDisplayValue('');
      return;
    }
    
    let price = parseFloat(value);
    if (isNaN(price)) {
      setDisplayValue('0.00');
      return;
    }
    
    // Apply VAT if needed for display
    if (isVatInclusive) {
      price *= (1 + (vatRate / 100));
    }
    
    setDisplayValue(price.toFixed(2));
  }, [value, isVatInclusive, vatRate]);
  
  // Handle input change
  const handleChange = (e) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
  };
  
  // Handle blur - convert display value to actual value
  const handleBlur = () => {
    let price = parseFloat(displayValue);
    
    if (isNaN(price)) {
      onChange('0.00');
      return;
    }
    
    // Remove VAT if needed for storage
    if (isVatInclusive) {
      price = price / (1 + vatRate / 100);
    }
    
    onChange(price.toFixed(2));
  };
  
  return (
    <TextField
      label={label}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      InputProps={{
        startAdornment: <InputAdornment position="start">£</InputAdornment>,
      }}
      inputProps={{
        inputMode: 'decimal',
        pattern: '[0-9]*\\.?[0-9]*'
      }}
      disabled={disabled}
      size="small"
      fullWidth
      sx={sx}
    />
  );
};

export default PriceField;